
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, ShoppingBag, Plus, Minus, Trash2, ArrowRight, Sparkles } from 'lucide-react';
import { useApp } from '../App';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebase';

const SideCart: React.FC = () => {
  const { cart, isCartOpen, setIsCartOpen, removeFromCart, updateCartQuantity } = useApp();
  const navigate = useNavigate();
  const [shippingConfig, setShippingConfig] = useState({ threshold: 250, fee: 45 });

  useEffect(() => {
    onValue(ref(db, 'settings/shipping'), (snapshot) => {
      if (snapshot.exists()) setShippingConfig(snapshot.val());
    });
  }, []);

  const subtotal = cart.reduce((acc, item) => acc + (item.product.price * item.quantity), 0);
  const shippingFee = subtotal >= shippingConfig.threshold ? 0 : shippingConfig.fee;
  const total = subtotal + shippingFee;

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />

      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
          {/* Header */}
          <div className="p-6 border-b border-stone-100 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ShoppingBag className="text-amber-700" size={20} />
              <h2 className="text-xl font-serif font-bold text-stone-900">Sepetim</h2>
              <span className="bg-stone-100 text-stone-500 text-[10px] font-black px-2 py-1 rounded-full">{cart.length}</span>
            </div>
            <button 
              onClick={() => setIsCartOpen(false)}
              className="p-2 text-stone-400 hover:text-stone-900 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Cart List */}
          <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center text-stone-300">
                  <ShoppingBag size={32} />
                </div>
                <div>
                  <p className="text-stone-900 font-bold">Sepetiniz Boş</p>
                  <p className="text-stone-400 text-sm mt-1">Evinize ruh katacak parçaları seçmeye başlayın.</p>
                </div>
                <button 
                  onClick={() => setIsCartOpen(false)}
                  className="mt-4 px-8 py-3 bg-stone-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-amber-800 transition-all"
                >
                  Keşfetmeye Başla
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {cart.map((item) => (
                  <div key={item.product.id} className="flex space-x-4 group">
                    <div className="w-24 h-24 bg-stone-50 rounded-2xl p-2 flex-shrink-0 overflow-hidden">
                      <img 
                        src={item.product?.images?.[0] || 'https://via.placeholder.com/150'} 
                        alt={item.product.name}
                        className="w-full h-full object-cover rounded-lg group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0 py-1">
                      <div className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-1">{item.product.brand || 'Lumina'}</div>
                      <h3 className="font-serif font-bold text-stone-900 truncate mb-1">{item.product.name}</h3>
                      <div className="text-amber-800 font-black mb-3">₺{item.product.price.toLocaleString()}</div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center bg-stone-50 rounded-lg p-1 border border-stone-100">
                          <button 
                            onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                            className="w-6 h-6 flex items-center justify-center text-stone-400 hover:text-amber-800"
                          ><Minus size={14} /></button>
                          <span className="w-8 text-center text-xs font-bold text-stone-900">{item.quantity}</span>
                          <button 
                            onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                            className="w-6 h-6 flex items-center justify-center text-stone-400 hover:text-amber-800"
                          ><Plus size={14} /></button>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.product.id)}
                          className="text-stone-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {cart.length > 0 && (
            <div className="p-6 border-t border-stone-100 bg-[#FAF8F5]">
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-stone-500 text-sm font-medium">
                  <span>Ara Toplam</span>
                  <span>₺{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-stone-500 text-sm font-medium">
                  <span>Kargo</span>
                  {shippingFee === 0 ? (
                    <span className="text-amber-700 font-bold uppercase text-[10px] tracking-widest">Ücretsiz</span>
                  ) : (
                    <span className="text-stone-900 font-bold text-sm">₺{shippingFee.toLocaleString()}</span>
                  )}
                </div>
                {shippingFee > 0 && (
                   <p className="text-[9px] text-amber-700 font-bold text-right italic">
                     ₺{(shippingConfig.threshold - subtotal).toLocaleString()} TL Daha Kargo Bedava!
                   </p>
                )}
                <div className="flex justify-between items-end pt-2">
                  <span className="text-stone-900 font-serif font-bold text-xl">Toplam</span>
                  <span className="text-2xl font-black text-amber-800">₺{total.toLocaleString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <button 
                  onClick={() => {
                    setIsCartOpen(false);
                    navigate('/checkout');
                  }}
                  className="w-full bg-amber-800 text-white py-4 rounded-2xl font-bold flex items-center justify-center space-x-3 hover:bg-amber-900 shadow-xl shadow-amber-900/20 transition-all active:scale-95"
                >
                  <Sparkles size={18} />
                  <span>Siparişi Onayla</span>
                </button>
                <button 
                  onClick={() => {
                    setIsCartOpen(false);
                    navigate('/cart');
                  }}
                  className="w-full bg-white border border-stone-200 text-stone-600 py-4 rounded-2xl font-bold text-sm hover:bg-stone-50 transition-all flex items-center justify-center"
                >
                  Sepete Git <ArrowRight size={16} className="ml-2" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SideCart;
